import {
    Component, OnInit, OnDestroy, Input, Output, EventEmitter,
    ChangeDetectorRef, HostListener
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import * as _ from 'lodash';

import { AdvancedSortService } from '../services/advanced-sort.service';
import { ColumnOption } from '../services/interfaces';
import { ISubTableSelectedObject } from '../services/interfaces';
import { DocumentViewerDialogComponent } from '../document-viewer-dialog/document-viewer-dialog.component';

/**
 * Generic table component � fully decoupled from any application services.
 *
 * Key differences from the admin-client's TableComponent:
 * - NO AppServices / EmployeeService / VendorService / etc. injected.
 * - Lookup data (account managers, employees, �) is supplied by the PARENT
 *   via the `lookupData` @Input rather than being loaded inside ngOnInit.
 * - File viewing is handled by an optional `fileUrlResolver` @Input function.
 *   If not provided the component emits `fileViewRequested` and the parent
 *   handles the dialog itself.
 */
@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnDestroy {

readonly dateFormats = { DISPLAY_FORMAT: 'MM/dd/yyyy', LOCALE: 'en-US' };

// ?? Inputs ????????????????????????????????????????????????????????????????

    private _headerList: any[] = [];
    @Input()
    set headerList(data: any) {
        this.key = null;
        this._headerList = data ? data.map((h: any) => ({ ...h })) : [];
    }
    get headerList(): any { return this._headerList; }

    private dataList: any[] = [];
    @Input()
    set dataArray(data: any) {
        if (data === null) { return; }
        this.dataList = data;
        this.isLoader = true;
        this.isAllSelected = false;
    }
    get dataArray(): any {
        if (!this.dataList?.length) { return this.dataList; }

        const advancedRules = this.advancedSortService.getSortRules();
        if (advancedRules.some(r => r.enabled)) {
            return this.advancedSortService.applySorting(this.dataList);
        }
        if (!this.key) { return this.dataList; }
        if (this.lastSortKey !== this.key || this.lastSortReverse !== this.reverse || !this.sortedDataCache.length) {
            this.sortedDataCache = _.orderBy(this.dataList, [this.key], [this.reverse ? 'asc' : 'desc']);
            this.lastSortKey = this.key;
            this.lastSortReverse = this.reverse;
        }
        return this.sortedDataCache;
    }

    /**
     * All lookup data needed for rendering dropdown/label columns (account
     * managers, employee lists, etc.). The parent component is responsible for
     * populating this from its own services.
     */
    @Input() lookupData: any = {};

    /**
     * Optional function to resolve a file path to a viewable URL.
     * Signature: (filePath: string) => Observable<string>
     * If omitted, `fileViewRequested` is emitted instead.
     */
    @Input() fileUrlResolver: ((path: string) => Observable<string>) | null = null;

    // ?? Outputs ???????????????????????????????????????????????????????????????

    @Output() buttonClick          = new EventEmitter<any>();
    @Output() deleteButtonClick    = new EventEmitter<any>();
    @Output() checkboxClick        = new EventEmitter<any>();
    @Output() allCheckboxClick     = new EventEmitter<any>();
    @Output() openAdvancedSort     = new EventEmitter<void>();
    @Output() openColumnAdvancedSort = new EventEmitter<any>();
    /** Emitted when no fileUrlResolver is bound and the user opens a file. */
    @Output() fileViewRequested    = new EventEmitter<any>();

    // ?? State ?????????????????????????????????????????????????????????????????

    isLoader: boolean = false;
    public reverse: boolean = true;
    public key: string;
    public isAllSelected = false;
    public isSortModalOpen: boolean = false;
    public selectedColumn: any = null;
    public availableColumns: ColumnOption[] = [];
    public page: number = 1;
    public itemsPerPage: number = 10;

    public columnSelectionOpen: boolean = false;
    public colSearchTerm: string = '';
    private draftHeaders: any[] = [];
    public selectedOrderList: any[] = [];
    private dragIndex: number = -1;

    private subscriptions: Subscription[] = [];
    private lastSortKey: string;
    private lastSortReverse: boolean;
    private sortedDataCache: any[] = [];

    constructor(
        private router: Router,
        private cdr: ChangeDetectorRef,
        public advancedSortService: AdvancedSortService,
        private dialog: MatDialog
    ) {}

    // ?? Lifecycle ?????????????????????????????????????????????????????????????

    ngOnInit(): void {
        this.buildAvailableColumns();

        this.subscriptions.push(
            this.advancedSortService.sortRules$.subscribe(() => {
                this.cdr.detectChanges();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    // ?? Rendering helpers ?????????????????????????????????????????????????????

    getCss(css: string): string {
        if (css === 'center') { return 'text-center'; }
        if (css === 'right')  { return 'text-right'; }
        if (css === 'left')   { return 'text-left'; }
        return '';
    }

    getColumnValue(header: any, data: any): string {
        return data[header['entityName']];
    }

    edit(data: any, type: string, entityName: string): void {
        const obj: ISubTableSelectedObject = { type, data, entityName };
        this.buttonClick.emit(obj);
    }

    routeToAnotherPage(routeData: any, data: any): void {
        const id = data[routeData.routeId];
        const childRouteId = data[routeData.childRouteId];
        const nestedChildRouteId = data[routeData.nestedchildRouteId];

        if (nestedChildRouteId) {
            this.router.navigate([routeData.routePath, id, childRouteId, nestedChildRouteId], { queryParams: { type: 'route' } });
        } else if (childRouteId) {
            this.router.navigate([routeData.routePath, id, childRouteId], { queryParams: { type: 'route' } });
        } else {
            this.router.navigate([routeData.routePath, id], { queryParams: { type: 'route' } });
        }
    }

    deleteRow(data: any): void { this.deleteButtonClick.emit(data); }

    sort(key: string): void { this.key = key; this.reverse = !this.reverse; }

    // ?? File viewing ??????????????????????????????????????????????????????????

    openFile(data: any): void { 
        console.log("?? OPENFILE CALLED", data);
        if (!this.fileUrlResolver) {
            // Delegate to the parent � no resolver bound
            this.fileViewRequested.emit(data);
            return;
        }

        this.fileUrlResolver(data.fileLocationPath).subscribe({
            next: (resp: any) => {
                let fileUrl: string = typeof resp === 'string' ? resp
                    : resp?.data ?? resp?.url ?? '';

                fileUrl = fileUrl.trim().replace(/^["']|["']$/g, '');

                console.log('?? Final cleaned file URL:', fileUrl);

                if (!fileUrl) { 
                    console.error('?? No valid file URL after cleanup');
                    return; 
                }

                const dialogConfig = {
                    width: '90vw', 
                    maxWidth: '1200px', 
                    height: '90vh',
                    panelClass: 'document-viewer-dialog-container',
                    hasBackdrop: true,
                    disableClose: false,
                    data: {
                        fileUrl,
                        fileName: data.fileName || data.documentName || 'Document',
                        fileType: (fileUrl.match(/\.([^.?#]+)(?:[?#]|$)/) || [])[1] ?? ''
                    }
                };

                console.log('?? Opening dialog with config:', dialogConfig);

                const dialogRef = this.dialog.open(DocumentViewerDialogComponent, dialogConfig);

                console.log('?? Dialog ref created:', dialogRef);

                dialogRef.afterOpened().subscribe(() => {
                    console.log('??? DIALOG OPENED SUCCESSFULLY');
                });

                dialogRef.afterClosed().subscribe(() => {
                    console.log('?? Dialog closed');
                });
            },
            error: (err: any) => console.error('SharedComponents TableComponent: file URL error', err)
        });
    }

    // ?? Selection ?????????????????????????????????????????????????????????????

    selectAll(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.dataList.forEach(item => item.selected = checked);
        this.isAllSelected = checked;
        this.allCheckboxClick.emit(this.dataList);
    }

    onCheckboxChange(index: number, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.dataList[index].selected = checked;
        this.isAllSelected = this.dataList.every(item => item.selected);
        this.checkboxClick.emit(this.dataList[index]);
    }

    // ?? Sort / filter ?????????????????????????????????????????????????????????

    buildAvailableColumns(): void {
        if (!this.headerList?.length) { return; }
        this.availableColumns = this.headerList
            .filter((h: any) => h.type !== 'checkbox' && h.type !== 'actions')
            .map((h: any) => {
                const col: ColumnOption = {
                    name: h.entityName,
                    displayName: h.headerName,
                    sortable: true,
                    filterable: !!h.filterable,
                    filterType: h.filterType || 'text'
                };
                if (h.filterable && h.filterType === 'dropdown' && h.configKey) {
                    const items = this.lookupData?.[h.configKey] ?? [];
                    const labelKey = h.labelKey || 'name';
                    const valueKey = h.valueKey || 'id';
                    col.options = items.map((item: any) => ({
                        label: item[labelKey] ?? item,
                        value: item[valueKey] ?? item
                    }));
                }
                return col;
            });
    }

    openSortModal(): void {
        // Don't build columns here - parent manages columns
        this.isSortModalOpen = true;
        this.selectedColumn = null;
        this.openAdvancedSort.emit();
    }

    openColumnSearch(header: any): void {
        // Don't build columns here - parent manages columns
        this.isSortModalOpen = true;
        this.selectedColumn = header;
        this.openColumnAdvancedSort.emit(header);
    }

    closeSortModal(): void {
        this.isSortModalOpen = false;
        this.selectedColumn = null;
    }

    applyAdvancedSort(_config: any): void {
        this.closeSortModal();
        this.cdr.detectChanges();
    }

    onHeaderClick(_header: any): void { /* sort now via column search icon */ }

    sortColumn(columnName: string, direction: 'asc' | 'desc'): void {
        const existing = this.advancedSortService.getSortRules().find(r => r.columnName === columnName);
        if (existing) { this.advancedSortService.updateSortOrder(existing.id, direction); }
        else {
            const header = this.headerList.find((h: any) => h.entityName === columnName);
            this.advancedSortService.addSortRule(columnName, header?.headerName ?? columnName, direction);
        }
        if (this.key === columnName) { this.key = null; }
        this.cdr.detectChanges();
    }

    clearSort(columnName: string): void {
        const existing = this.advancedSortService.getSortRules().find(r => r.columnName === columnName);
        if (existing) { this.advancedSortService.removeSortRule(existing.id); }
        if (this.key === columnName) { this.key = null; this.reverse = true; }
        this.cdr.detectChanges();
    }

    clearAllSorts(): void {
        this.advancedSortService.clearAllRules();
        this.key = null; this.reverse = true;
        this.cdr.detectChanges();
    }

    isSorted(columnName: string): boolean {
        return this.advancedSortService.getSortRules().some(r => r.columnName === columnName && r.enabled)
            || this.key === columnName;
    }

    getSortDirection(columnName: string): 'asc' | 'desc' | null {
        const rule = this.advancedSortService.getSortRules().find(r => r.columnName === columnName && r.enabled);
        if (rule) { return rule.order; }
        return this.key === columnName ? (this.reverse ? 'asc' : 'desc') : null;
    }

    getSortPriority(columnName: string): number {
        const idx = this.advancedSortService.getSortRules().findIndex(r => r.columnName === columnName && r.enabled);
        return idx >= 0 ? idx + 1 : 0;
    }

    getActiveSortCount(): number {
        return this.advancedSortService.getSortRules().filter(r => r.enabled).length;
    }

    changePageSize(size: number): void { this.itemsPerPage = size; this.page = 1; }

    // ?? Getters ???????????????????????????????????????????????????????????????

    get visibleHeaders(): any[] { return this._headerList.filter(h => h.visible !== false); }
    get pickableHeaders(): any[] { return this._headerList.filter(h => h.type !== 'checkbox' && h.type !== 'actions'); }
    get filteredDraftHeaders(): any[] {
        const term = this.colSearchTerm.trim().toLowerCase();
        return term ? this.draftHeaders.filter(h => (h.headerName || '').toLowerCase().includes(term))
                    : this.draftHeaders;
    }
    get selectedCount(): number  { return this.draftHeaders.filter(h => h._checked).length; }
    get totalPickable(): number  { return this.draftHeaders.length; }
    get allChecked(): boolean    { return this.draftHeaders.length > 0 && this.draftHeaders.every(h => h._checked); }
    get someChecked(): boolean   { const c = this.selectedCount; return c > 0 && c < this.totalPickable; }

    // ?? Column editor ?????????????????????????????????????????????????????????

    openColumnEditor(): void {
        this.draftHeaders = this.pickableHeaders.map(h => ({ ...h, _checked: h.visible !== false }));
        this.colSearchTerm = '';
        this.selectedOrderList = this.draftHeaders.filter(h => h._checked).map(h => ({ ...h }));
        this.columnSelectionOpen = true;
    }

    closeColumnEditor(): void { this.columnSelectionOpen = false; }

    applyColumnSelection(): void {
        const orderMap = new Map<string, number>();
        this.selectedOrderList.forEach((h, i) => orderMap.set(h.entityName, i));
        const checkedMap = new Map<string, boolean>();
        this.draftHeaders.forEach(h => checkedMap.set(h.entityName, h._checked));
        const systemCols = this._headerList.filter(h => h.type === 'checkbox' || h.type === 'actions');
        const ordered = [...this.pickableHeaders].sort((a, b) =>
            (orderMap.get(a.entityName) ?? 9999) - (orderMap.get(b.entityName) ?? 9999));
        ordered.forEach(h => { h.visible = checkedMap.get(h.entityName) ?? true; });
        this._headerList = [...systemCols, ...ordered];
        this.columnSelectionOpen = false;
    }

    toggleSelectAll(checked: boolean): void {
        this.draftHeaders.forEach(h => h._checked = checked);
        this._rebuildSelectedOrderList();
    }

    toggleDraftColumn(header: any): void { header._checked = !header._checked; this._rebuildSelectedOrderList(); }

    removeFromSelected(header: any): void {
        const draft = this.draftHeaders.find(d => d.entityName === header.entityName);
        if (draft) { draft._checked = false; }
        this.selectedOrderList = this.selectedOrderList.filter(h => h.entityName !== header.entityName);
    }

    onDragStart(index: number): void { this.dragIndex = index; }
    onDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        if (this.dragIndex === -1 || this.dragIndex === index) { return; }
        const moved = this.selectedOrderList.splice(this.dragIndex, 1)[0];
        this.selectedOrderList.splice(index, 0, moved);
        this.dragIndex = index;
    }
    onDragEnd(): void { this.dragIndex = -1; }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.col-editor-panel') && !target.closest('.btn-col-picker')) {
            this.columnSelectionOpen = false;
        }
    }

    private _rebuildSelectedOrderList(): void {
        this.selectedOrderList = this.selectedOrderList.filter(h => {
            const draft = this.draftHeaders.find(d => d.entityName === h.entityName);
            return draft?.checked;
        });
        const existingKeys = new Set(this.selectedOrderList.map(h => h.entityName));
        this.draftHeaders.forEach(h => {
            if (h._checked && !existingKeys.has(h.entityName)) {
                this.selectedOrderList.push({ ...h });
            }
        });
    }
}
